import { createI18n } from 'vue-i18n'

import ko from '@/core/i18n/locales/ko.json'

export const SUPPORTED_LOCALES = ['ko', 'en', 'ja', 'zh', 'th', 'vi'] as const
export type Locale = (typeof SUPPORTED_LOCALES)[number]

export const FALLBACK_LOCALE: Locale = 'ko'

/** localStorage 캐시 키 — 원본 i18next-browser-languagedetector 기본값과 동일 */
const STORAGE_KEY = 'i18nextLng'

/**
 * 항상 노출되는 언어. th/vi는 매장 culture가 맞을 때만 추가로 노출한다.
 * (원본 LanguageModal 규칙)
 */
export const ALWAYS_AVAILABLE_LOCALES: Locale[] = ['en', 'ko', 'zh', 'ja']

export function localesForCulture(culture: string | undefined): Locale[] {
  const list = [...ALWAYS_AVAILABLE_LOCALES]
  if (culture === 'th-TH') list.push('th')
  if (culture === 'vi-VN') list.push('vi')
  return list
}

function isSupported(value: string | null | undefined): value is Locale {
  return !!value && (SUPPORTED_LOCALES as readonly string[]).includes(value)
}

/**
 * 언어 감지. 원본은 i18next 기본 감지 순서에 암묵적으로 의존했다.
 * 여기서는 같은 순서를 명시적으로 구현한다 (querystring → localStorage → navigator).
 */
export function detectLocale(): Locale {
  const fromQuery = new URLSearchParams(window.location.search).get('lng')
  if (isSupported(fromQuery)) return fromQuery

  const stored = localStorage.getItem(STORAGE_KEY)
  if (isSupported(stored)) return stored

  // 'ko-KR' → 'ko' (원본 i18next load: 'languageOnly' 동작)
  for (const lang of navigator.languages ?? [navigator.language]) {
    const base = lang?.split('-')[0]
    if (isSupported(base)) return base
  }

  return FALLBACK_LOCALE
}

/**
 * 폴백 언어(ko)만 번들에 포함하고 나머지는 필요할 때 가져온다.
 * 6개를 전부 넣으면 초기 번들이 불필요하게 커진다 (Plan 성능 NFR).
 */
const LOADERS: Record<Locale, () => Promise<{ default: Record<string, unknown> }>> = {
  ko: () => Promise.resolve({ default: ko }),
  en: () => import('@/core/i18n/locales/en.json'),
  ja: () => import('@/core/i18n/locales/ja.json'),
  zh: () => import('@/core/i18n/locales/zh.json'),
  th: () => import('@/core/i18n/locales/th.json'),
  vi: () => import('@/core/i18n/locales/vi.json'),
}

/** ko를 기준 스키마로 삼는다 — 나머지 언어는 동일 키를 갖는지 테스트로 보장한다 */
export type MessageSchema = typeof ko

/*
 * 제네릭을 명시하지 않으면 vue-i18n이 초기 messages({ ko })만 보고 locale 타입을
 * 'ko'로 고정해버려 다른 언어로 전환할 수 없다.
 */
// 세 번째 제네릭(Legacy=false)을 빼면 legacy 오버로드로 잡혀 locale이 Ref가 아니게 된다
export const i18n = createI18n<[MessageSchema], Locale, false>({
  legacy: false,
  locale: FALLBACK_LOCALE,
  fallbackLocale: FALLBACK_LOCALE,
  messages: { ko } as Record<Locale, MessageSchema>,
  missingWarn: false,
  fallbackWarn: false,
})

const loaded = new Set<Locale>([FALLBACK_LOCALE])

async function ensureLoaded(locale: Locale) {
  if (loaded.has(locale)) return
  const messages = await LOADERS[locale]()
  i18n.global.setLocaleMessage(locale, messages.default as MessageSchema)
  loaded.add(locale)
}

/**
 * 언어 전환.
 *
 * 원본은 enum 표시명을 모듈 로드 시점에 t()로 고정해버려서 전환할 때마다
 * window.location.reload()가 필요했다. 신규는 표시명을 화면에서 t()로 해석하므로
 * 새로고침 없이 전환된다. 매장 데이터만 새 langCode로 다시 받으면 된다. (Design §8.5)
 */
export async function setLocale(locale: Locale) {
  await ensureLoaded(locale)
  i18n.global.locale.value = locale
  localStorage.setItem(STORAGE_KEY, locale)
  // 폰트가 lang 속성으로 분기된다 (ja → Noto Sans JP)
  document.documentElement.lang = locale
}

export function currentLocale(): Locale {
  return i18n.global.locale.value as Locale
}

/** 앱 부팅 시 감지된 언어를 적용한다 */
export function initLocale() {
  return setLocale(detectLocale())
}
